<template>
  <div class="page play-page">
    <!-- Screen flash overlay — fires for ~80ms on wins. Roobet/Stake pattern:
         even peripheral vision picks it up. Skipped in auto mode to avoid
         epilepsy-grade strobing. -->
    <div v-if="winFlash" class="win-flash" />

    <!-- Top HUD: skin selector (left) + P&L pill (right). -->
    <div class="top-hud">
      <div class="skin-selector">
        <button
          v-for="skin in skins"
          :key="skin.id"
          class="skin-chip"
          :class="{ selected: currentSkinId === skin.id }"
          :title="skin.name"
          @click="selectSkin(skin.id)"
        >
          <span class="skin-icon">{{ skin.icon }}</span>
        </button>
      </div>
      <div class="hud-right">
        <button class="history-btn" title="Game history" @click="openHistory">&#9827;</button>
        <router-link to="/how-it-works" class="history-btn help-btn" title="How it works">?</router-link>
        <!-- Wallet balance folded into the one HUD (the global float-pill is hidden
             on this route) so there's a single top nav, not two overlapping ones. -->
        <button class="wallet-chip mono" @click="$emit('open-wallet')" :title="`Wallet — ${arkStatus}`">
          <span class="conn-dot" :class="arkStatus"></span>
          <span class="wallet-ico" aria-hidden="true">&#128176;</span>
          <span class="wallet-bal">{{ walletBalanceLabel }}</span>
        </button>
      </div>
    </div>

    <!-- Trustless safety net: if a game ever fails to resolve, the player's
         escrowed stake is reclaimable here (no trust in the server). -->
    <StalledBets />

    <!-- ONE always-present status row: last-50 results + the streak badge.
         Both used to come and go independently — and the streak was absolutely
         positioned at a hardcoded top:120px, which now lands on the wheel — so
         the top of the screen moved every time a streak started or the first
         result arrived. The row reserves its height whether or not it has
         content, so nothing below it shifts. -->
    <div class="status-row">
      <!-- P/L lives here rather than in the top row: with it up there, row 1
           could not fit on a phone without either wrapping (the original
           behaviour) or clipping the last skin chip (what tightening it did).
           Moving one item down solves both, and P/L is status anyway. -->
      <div class="pnl-pill" :class="pnlClass" @click="togglePnlScope" :title="`Click to switch — currently ${pnlScope}`">
        <span class="pnl-scope">{{ pnlScope }}</span>
        <span class="pnl-amount mono">{{ formattedPnl }}</span>
      </div>
      <div class="sparkline" v-if="sparkline.length">
        <span
          v-for="(r, i) in sparkline"
          :key="r.key"
          class="spark-dot"
          :class="r.won ? 'win' : 'loss'"
          :style="{ animationDelay: `${(sparkline.length - 1 - i) * 12}ms` }"
        />
      </div>
      <transition name="streak-pop">
        <div v-if="streak.count >= 3" class="streak-badge" :class="streak.kind">
          <span class="streak-icon">{{ streak.kind === 'win' ? '🔥' : '❄' }}</span>
          STREAK ×{{ streak.count }}
        </div>
      </transition>
    </div>

    <!-- Centerpiece skin. For skins that own their play gesture (Rocket),
         pass the extra props the skin needs and bind its launch/cashout
         events; non-gesture skins ignore the unknown props. -->
    <div class="skin-area" :class="{ playing: isFlipping }">
      <component
        :is="currentSkin.component"
        :state="skinState"
        :tier="betAmount"
        :odds-edge-bps="oddsEdgeBps"
        :dust="dust"
        @launch="onSkinLaunch"
        @cashout="onSkinCashout"
      />
    </div>

    <div class="controls" :class="{ inert: isFlipping && !isAutoRunning }">
      <div class="control-group amount-slider">
        <div class="amount-readout">
          <span class="amount-label">Bet</span>
          <!-- The value IS the input — typing is the precise alternative to
               dragging, and replacing the readout in place costs no height. -->
          <span class="amount-value">
            <NumberField
              :model-value="betAmount"
              :format="formatSats"
              :disabled="isAutoRunning || !canBet"
              aria-label="Bet amount in sats"
              @commit="commitAmount"
            />
            sats
          </span>
        </div>
        <input
          class="amount-range"
          type="range"
          :min="amountBounds.min"
          :max="usableMax"
          step="1"
          v-model.number="betAmount"
          @change="rememberAmount"
          :disabled="isAutoRunning || !canBet"
          aria-label="Bet amount"
        />
        <div class="amount-ends">
          <span>{{ tiersLoaded ? amountBounds.min.toLocaleString() : '—' }}</span>
          <span v-if="!tiersLoaded">Loading bet range…</span>
          <!-- ORDER MATTERS, and it is about which lever the player can pull.
               A high win chance needs a BIG stake (the house side must clear
               dust), so a modest balance is blocked at 95% but fine at 50% —
               lowering the win chance is the fix, and it is the odds slider, not
               this one. Reported live at 95% with 4,790 sats against a 6,464
               minimum: the amount slider went inert and the row said the house
               couldn't cover, so the one control that would have unstuck the
               player looked irrelevant. Both constraints were in fact binding;
               naming only the house's hid the actionable one.
               So: if a LOWER win chance would work, say that first. -->
          <span v-else-if="lowerOddsWouldHelp" class="amount-warn">
            Lower the win chance — {{ winPctValue }}% needs {{ amountBounds.min.toLocaleString() }} sats
          </span>
          <!-- feasible BEFORE canAffordRung, as it always was: canAffordRung
               folds both reasons into one flag (`feasible && balance >= min`),
               so checking it first misreports a house shortfall as the player's
               wallet. Verified: at capacity 340 no rung is feasible at all, and
               the inverted order claimed "balance too low" when the house was
               the blocker. Only `lowerOddsWouldHelp` belongs ahead of both,
               because it is the one case with a lever the player can pull. -->
          <span v-else-if="!amountBounds.feasible" class="amount-warn">House can't cover a bet at this win chance</span>
          <span v-else-if="!canAffordRung" class="amount-warn">
            Balance too low — add funds to bet at {{ winPctValue }}%
          </span>
          <span v-else-if="!canBet" class="amount-warn">House has no capacity right now</span>
          <span v-else-if="balanceCapped" class="amount-warn">Capped by your wallet balance</span>
          <span>{{ tiersLoaded && canAffordRung ? usableMax.toLocaleString() : '—' }}</span>
        </div>
      </div>

      <!-- Odds slider: walks the active skin's bet ladder — slide right for
           more coins / reels / dice (lower win rate, bigger payout). The exact
           win chance + multiple update live; all enforced on-chain. The top end
           is clamped to what the house bankroll can cover for this stake. -->
      <!-- Visible in auto mode too — the player needs to see (and set) the odds
           and payout they're auto-betting at; the input only locks while a batch
           is actually running. -->
      <div class="odds-slider">
        <div class="odds-readout">
          <span class="odds-step-label">{{ stepLabel }}</span>
          <span class="odds-stats">
            <span class="win-pct">
              <NumberField
                :model-value="winPctValue"
                :disabled="isAutoRunning"
                aria-label="Win chance percent"
                @commit="commitWinPct"
              />% win
            </span>
            <span class="payout-mult">{{ payoutMult(selectedBet) }}</span>
            <span class="win-amt" v-if="canBet">→ {{ winSatsLabel(selectedBet) }}</span>
          </span>
        </div>
        <input
          class="odds-range"
          type="range"
          :min="minStep"
          :max="maxStep"
          step="1"
          v-model.number="sliderIndex"
          @change="rememberStep"
          :disabled="isAutoRunning"
          aria-label="Odds"
        />
        <div class="odds-ends">
          <span>safer · bigger chance</span>
          <span>riskier · bigger payout</span>
        </div>
      </div>

      <!-- Auto-batch + FLIP button only when the skin doesn't own the
           play gesture. Rocket renders its own LAUNCH / CASH OUT inside
           the centerpiece. -->
      <template v-if="!skinOwnsGesture">
        <div class="auto-selector">
          <button
            v-for="opt in autoOptions"
            :key="opt.label"
            class="auto-chip"
            :class="{ selected: autoCount === opt.value }"
            :disabled="isAutoRunning"
            @click="selectAuto(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>

        <button
          class="flip-btn"
          :class="{ active: canFlip, stop: isAutoRunning, stopping: stopRequested }"
          :disabled="(!canFlip && !isAutoRunning) || stopRequested"
          @click="doFlip"
        >
          <template v-if="stopRequested">STOPPING AFTER THIS FLIP…</template>
          <template v-else-if="isAutoRunning">STOP · {{ autoProgressLabel }}</template>
          <template v-else-if="isAutoMode">{{ isFlipping ? flipStageLabel : `AUTO ×${autoCountLabel}` }}</template>
          <template v-else>{{ isFlipping ? flipStageLabel : 'FLIP IT' }}</template>
        </button>

        <!-- During autoplay the button is the STOP control and shows the batch
             count, so the stage goes here instead — otherwise the one case the
             wait feels longest (a whole batch) is the one with no feedback.
             The dot animates, so even a slow leg is visibly still moving. -->
        <div v-if="isFlipping && isAutoRunning" class="flip-stage-hint">
          <span class="stage-dot" aria-hidden="true"></span>{{ flipStageLabel }}
        </div>
        <div class="hotkey-hint" v-else-if="!isAutoMode && !isFlipping && canFlip">Press Enter to flip</div>
      </template>
    </div>

    <div v-if="error" class="error-toast" @click="error = null" title="Tap to dismiss">{{ error }}</div>

    <!-- Result slab — Stake pattern. Slides up from bottom, auto-dismisses. -->
    <transition name="slab">
      <div v-if="slab" class="result-slab" :class="slab.won ? 'win' : 'loss'">
        <div class="slab-label">{{ slab.won ? 'YOU WIN' : 'YOU LOSE' }}</div>
        <div class="slab-amount mono">
          {{ slab.won ? '+' : '−' }}{{ Math.abs(slab.amount).toLocaleString() }}
          <span class="slab-unit">sats</span>
        </div>
      </div>
    </transition>

    <!-- Game history modal — scrollable list within a fixed card, not the page. -->
    <transition name="hist-fade">
      <div v-if="historyOpen" class="hist-backdrop" @click.self="historyOpen = false">
        <div class="hist-modal" role="dialog" aria-label="Game history">
          <header class="hist-header">
            <h3 class="hist-title">Game History</h3>
            <button class="hist-close" @click="historyOpen = false" aria-label="Close">&times;</button>
          </header>
          <div class="hist-body">
            <GameHistoryList :games="historyGames" @select="openDetail" />
          </div>
        </div>
      </div>
    </transition>

    <!-- Per-game detail modal (txs, params, preimages, explorer links). Opened
         by clicking a history row. -->
    <GameDetailsModal
      :open="detailOpen"
      :game-id="detailGameId"
      :player-pubkey="playerPubkey"
      :network="networkName"
      @close="detailOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import GameHistoryList, { type GameHistoryItem } from '@/components/GameHistoryList.vue'
import GameDetailsModal from '@/components/GameDetailsModal.vue'
import StalledBets from '@/components/StalledBets.vue'
import { getTiers } from '@/services/api'
import { SKINS, getSavedSkinId, saveSkinId, findSkin, type SkinState, type OddsBet } from '@/skins'
import { getSavedBetAmount, saveBetAmount, getSavedStep, saveStep } from '@/utils/betPrefs'
import NumberField from '@/components/NumberField.vue'
import { winPctOf, roundWinPct, nearestRungByWinPct } from '@/utils/rungSnap'
import { getErrorMessage, friendlyError } from '@/utils/errors'
import { logDiag } from '@/utils/diagnosticsLog'
// House-stake / odds formula — single-sourced in the lib so this preview can't
// drift from what the server charges (crypto-free subpath, like game-math).
import { computeHouseStake, amountBoundsForOdds, feasibleOddsWindow } from 'arkade-coinflip/dist/stake-math'

const AUTO_OPTIONS = [
  { label: 'OFF', value: null },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '∞', value: Infinity },
] as const

const AUTO_FLIP_PAUSE_MS = 900
const SLAB_MS_MANUAL = 1800
const SLAB_MS_AUTO = 700
// Minimum time the flip animation runs once the bet is placed, so a fast
// server resolution still reads as a real flip rather than an instant snap.
const MIN_FLIP_MS = 900
const WIN_FLASH_MS = 120
const SPARKLINE_MAX = 50
const PNL_KEY = 'coinflip.pnl_alltime'

interface SparkResult { won: boolean; key: number }
interface SlabResult { won: boolean; amount: number }

export default defineComponent({
  name: 'PlayView',
  components: { GameHistoryList, GameDetailsModal, StalledBets, NumberField },
  emits: ['open-wallet'],
  setup(_props, { emit }) {
    const store = useStore()

    // ── Game config ───────────────────────────────────────────────────
    const tiers = ref<number[]>([1000, 5000, 10000, 50000])
    const maxAvailable = ref(50000)
    // House spendable — the real ceiling on a payout (sizes the odds slider).
    const houseBankroll = ref(0)
    // arkd dust limit + variable-odds house edge — used to clamp the slider's
    // SAFE end (a high-win bet's house stake must clear dust, or the server
    // rejects it). Defaults mirror the server.
    const dust = ref(546)
    const oddsEdgeBps = ref(300)
    const rakeType = ref<'percentage' | 'flat'>('percentage')
    const rakeValue = ref(2)
    const houseReady = ref(false)
    // Flips true after the first successful /api/tiers response. Before that,
    // capacity/betMin/betMax are still just defaults, not real bounds — the
    // template gates on this so the pre-load render is neutral rather than a
    // false "no capacity" / inverted-range flash.
    const tiersLoaded = ref(false)
    // Per-bet ceiling published by /api/tiers (reservation-aware). Distinct from
    // houseBankroll, which is the whole spendable balance.
    const capacity = ref(0)
    const betMin = ref(331)
    const betMax = ref(50_000)
    // The bet amount is now continuous. Starts at the stake the player last
    // used (or the low rail on a first visit); the watchers below keep it inside
    // the envelope as the odds or capacity move — which is also what makes
    // restoring a stale saved stake safe, since the real bounds only arrive with
    // /api/tiers and may well have moved since the last session.
    const betAmount = ref(getSavedBetAmount() ?? betMin.value)

    // ── Skin selection (early, so the odds slider can read the ladder) ──
    const currentSkinId = ref(getSavedSkinId())
    const currentSkin = computed(() => findSkin(currentSkinId.value))

    // ── Odds slider ───────────────────────────────────────────────────
    // The slider walks the active skin's bet ladder (strictly decreasing win
    // rate — more coins / reels / dice). `sliderIndex` is the position.
    const sliderIndex = ref(getSavedStep(currentSkinId.value) ?? currentSkin.value.defaultStep)
    const currentSkinLadder = computed(() => currentSkin.value.oddsLadder)
    // The house escrow for a bet, via the shared computeHouseStake
    // (house-edged): tier·(n−win)/win, trimmed by the edge. House stake RISES
    // with payout, so the ladder's playable window is a contiguous band:
    //   floor = first step whose stake clears dust (safe end, tiny stakes),
    //   ceiling = last step the bankroll can cover (risky end, big stakes).
    function houseStakeAt(tier: number, bet: OddsBet): number {
      return computeHouseStake(tier, bet.n, bet.target, bet.lo, oddsEdgeBps.value)
    }
    function houseStakeOf(bet: OddsBet): number {
      return houseStakeAt(betAmount.value, bet)
    }
    // Feasible ladder band for the current amount. Walking the ladder against
    // the real computeHouseStake is what keeps the client and server in exact
    // agreement — never offer a step /play would reject.
    const oddsWindow = computed(() =>
      feasibleOddsWindow(betAmount.value, currentSkinLadder.value, {
        edgeBps: oddsEdgeBps.value, dust: dust.value, capacity: capacity.value,
      }),
    )
    const minStep = computed(() => oddsWindow.value?.loIndex ?? 0)
    const maxStep = computed(() => oddsWindow.value?.hiIndex ?? currentSkinLadder.value.length - 1)
    const canBet = computed(() => oddsWindow.value !== null && capacity.value >= dust.value && canAffordRung.value)
    const amountBounds = computed(() =>
      amountBoundsForOdds(selectedBet.value, {
        edgeBps: oddsEdgeBps.value, dust: dust.value, capacity: capacity.value,
        railMin: betMin.value, railMax: betMax.value,
        // The bet the SERVER caps on is `tier + stakeTopUp`, not the slider's
        // value: placeTrustlessBet folds a sub-dust wallet change (≤ dust) into
        // the stake after the amount is already fixed. Bounding on the amount
        // alone 400s the player at the slider's own maximum whenever that fold
        // fires — intermittently, since it depends on their live coin shape.
        // Reserving dust here costs ≤ dust of top-end range and cannot 400.
        topUpHeadroom: dust.value,
      }),
    )
    const safeStep = computed(() => Math.min(Math.max(sliderIndex.value, minStep.value), maxStep.value))
    const selectedBet = computed<OddsBet>(() => currentSkinLadder.value[safeStep.value])
    const stepLabel = computed(() => currentSkin.value.stepLabel(selectedBet.value, safeStep.value))
    // The win chance as a NUMBER, already rounded for display — the odds field
    // both shows and edits this, so keeping it pre-rounded means focusing the
    // field reveals "32", not "32.432432432432435". Snapping still measures
    // against each rung's exact win chance, so nothing is lost.
    const winPctValue = computed(() => roundWinPct(winPctOf(selectedBet.value)))
    // ACTUAL payout multiple — what the player really receives, after the house
    // edge trims the house stake (computeHouseStake) AND the rake is taken off
    // the pot (calculateRake). The win% is exact (fees never touch probability);
    // this multiple is below the fair n/win because of the fees. Mirrors the
    // server economics so the slider shows the honest payout — e.g. a 50% coin
    // at 300 bps edge + 2% rake pays ~1.93×, not a fair 2×.
    function payoutMult(bet: OddsBet): string {
      const tier = betAmount.value
      if (!tier) return '—'
      const pot = tier + houseStakeAt(tier, bet)
      let rake = rakeType.value === 'percentage' ? Math.floor((pot * rakeValue.value) / 100) : rakeValue.value
      if (pot - rake < dust.value) rake = 0 // server waives a rake that would dust the payout
      const m = (pot - rake) / tier
      return m.toFixed(2).replace(/\.?0+$/, '') + '×'
    }
    /** NET sats the player gets back on a win, formatted with thousands
     *  separators (e.g. "1,650 sats"). What you actually pocket — not the
     *  pot, not the gross payout. Mirrors the server's settle math (same
     *  rake-floor + dust-waiver as `payoutMult`). */
    function winSatsLabel(bet: OddsBet): string {
      const tier = betAmount.value
      if (!tier) return ''
      const pot = tier + houseStakeAt(tier, bet)
      let rake = rakeType.value === 'percentage' ? Math.floor((pot * rakeValue.value) / 100) : rakeValue.value
      if (pot - rake < dust.value) rake = 0
      return `${(pot - rake).toLocaleString()} sats`
    }

    // ── Flip lifecycle state ──────────────────────────────────────────
    // `climbing` and `settling` are for skins that own their play gesture
    // (Rocket): launch → climb → cashout → settle. Standard skins only
    // ever see idle / flipping / resolved.
    const phase = ref<'idle' | 'flipping' | 'resolved' | 'climbing' | 'settling'>('idle')
    const outcome = ref<{ won: boolean; side: 'heads' | 'tails'; roll: number | null } | null>(null)
    const isFlipping = ref(false)
    /**
     * What the in-flight bet is actually waiting on, so an ~8s round trip reads
     * as progress rather than a frozen button. The wording names the leg in the
     * player's terms, not the endpoint's: "placing" is /api/v4/play (the ~5s
     * one), "funding" the co-fund handshake, "signing" the checkpoint finalize,
     * "settling" the reveal.
     */
    const FLIP_STAGE_TEXT: Record<string, string> = {
      placing: 'PLACING BET',
      funding: 'FUNDING POT',
      signing: 'SIGNING',
      settling: 'SETTLING',
    }
    const flipStageLabel = computed(() => {
      const s = store.state.ark?.flipStage
      // Falls back to the old wording until the first stage lands, so there's
      // never a blank button in the gap before /play is dispatched.
      return (s && FLIP_STAGE_TEXT[s]) || 'FLIPPING...'
    })
    const error = ref<string | null>(null)

    const skinState = computed<SkinState>(() => ({
      phase: phase.value,
      outcome: outcome.value,
      odds: selectedBet.value,
    }))

    // ── Auto-flip state ───────────────────────────────────────────────
    const autoCount = ref<number | null>(null)
    const autoRemaining = ref(0)
    const isAutoRunning = ref(false)
    const stopRequested = ref(false)
    const isAutoMode = computed(() => autoCount.value !== null)
    const autoCountLabel = computed(() => autoCount.value === Infinity ? '∞' : String(autoCount.value ?? ''))
    const autoRemainingLabel = computed(() => isFinite(autoRemaining.value) ? String(autoRemaining.value) : '∞')
    /** "DONE OF TOTAL" progress label during autoplay, e.g. "3 / 25". For
     *  infinite autoplay, shows the done count alone (no total). */
    const autoProgressLabel = computed(() => {
      const total = autoCount.value
      if (total === null) return ''
      if (!isFinite(total)) return `${autoFlipsDone.value} done`
      return `${autoFlipsDone.value} / ${total}`
    })
    const autoFlipsDone = computed(() => {
      const total = autoCount.value
      if (total === null || !isFinite(total)) return autoFlipsCounter.value
      return total - autoRemaining.value
    })
    /** Monotonically increasing counter for infinite (∞) autoplay. Resets
     *  on each new autoplay batch. */
    const autoFlipsCounter = ref(0)

    // ── Skin selection ────────────────────────────────────────────────
    // (currentSkinId / currentSkin are declared above with the odds slider.)
    const skinOwnsGesture = computed(() => currentSkin.value.ownsPlayGesture === true)
    function selectSkin(id: string) {
      currentSkinId.value = id
      saveSkinId(id)
      // Reset to idle so a half-played gesture in the previous skin
      // doesn't bleed into the new one.
      phase.value = 'idle'
      outcome.value = null
    }

    /** Skin emitted `launch` — start the climb. (Only the Rocket skin does this today.) */
    function onSkinLaunch() {
      if (isFlipping.value) return
      phase.value = 'climbing'
      outcome.value = null
      error.value = null
    }

    /** Skin emitted `cashout` with the locked-in bet — commit it via flipOnce. */
    async function onSkinCashout(bet: OddsBet) {
      if (isFlipping.value) return
      phase.value = 'settling'
      await flipOnce(bet)
    }

    // ── History modal ─────────────────────────────────────────────────
    const historyOpen = ref(false)
    const historyGames = ref<GameHistoryItem[]>([])
    function openHistory() {
      try {
        historyGames.value = JSON.parse(localStorage.getItem('gameHistory') || '[]')
      } catch {
        historyGames.value = []
      }
      historyOpen.value = true
    }

    // ── Per-game detail modal ─────────────────────────────────────────
    const detailOpen = ref(false)
    const detailGameId = ref('')
    function openDetail(gameId: string) {
      detailGameId.value = gameId
      detailOpen.value = true
    }
    // Player pubkey + network needed by GameDetailsModal — both are reactive
    // from the wallet/ark store so they stay current across wallet reconnects.
    const playerPubkey = computed<string>(() => store.state.wallet?.publicKey || '')
    const networkName = computed<string | null>(() => store.state.ark?.info?.network ?? null)

    // ── P&L (session + all-time) ──────────────────────────────────────
    const sessionPnl = ref(0)
    const allTimePnl = ref<number>((() => {
      try { return JSON.parse(localStorage.getItem(PNL_KEY) || '0') } catch { return 0 }
    })())
    const pnlScope = ref<'session' | 'alltime'>('session')
    function togglePnlScope() {
      pnlScope.value = pnlScope.value === 'session' ? 'alltime' : 'session'
    }
    const displayedPnl = computed(() => pnlScope.value === 'session' ? sessionPnl.value : allTimePnl.value)
    const formattedPnl = computed(() => {
      const n = displayedPnl.value
      const sign = n > 0 ? '+' : n < 0 ? '−' : ''
      return `${sign}${Math.abs(n).toLocaleString()}`
    })
    const pnlClass = computed(() => {
      if (displayedPnl.value > 0) return 'up'
      if (displayedPnl.value < 0) return 'down'
      return 'neutral'
    })

    // ── Streak ────────────────────────────────────────────────────────
    const streak = ref<{ kind: 'win' | 'loss'; count: number }>({ kind: 'win', count: 0 })

    // ── Sparkline + slab + flash ──────────────────────────────────────
    const sparkline = ref<SparkResult[]>([])
    const slab = ref<SlabResult | null>(null)
    let slabTimer: ReturnType<typeof setTimeout> | null = null
    const winFlash = ref(false)

    // ── Other computed ────────────────────────────────────────────────
    // Wallet balance + connection status for the HUD wallet chip (mirrors what
    // App.vue's float-pill shows, since that pill is hidden on this route).
    const arkStatus = computed(() => store.state.ark?.status as string)
    const walletBalanceLabel = computed(() => Number(store.getters['ark/balance'] || 0).toLocaleString())

    // Spendable offchain balance (settled + preconfirmed). Infinity only while
    // the wallet is still connecting (ark.walletBalance === null), so the UI
    // isn't gated on a stale-empty balance during boot.
    //
    // NB the `ark.` — the SDK balance object lives in the ark MODULE. The root
    // `state.walletBalance` is a stale legacy scalar (declared `number`,
    // initialised 0, and its SET_BALANCE mutation is never committed anywhere),
    // so reading it gave `(0)?.available` → undefined → Infinity and every
    // balance guard below silently did nothing: no wallet cap on the slider, no
    // "capped by your balance" / "too low" warning, and the affordability check
    // in flipOnce could never fire. Broken since 0.3.9; `useStore()` is untyped
    // here, so nothing flagged the wrong path.
    const playerBalance = computed(() => store.state.ark.walletBalance?.available ?? Infinity)
    // The player can never bet more than they hold, even when the house could
    // cover it. `amountBounds` stays the house-side envelope alone (so it's
    // still exactly what the server would independently compute); this folds
    // in the wallet cap for the slider's actual usable ceiling.
    const usableMax = computed(() => Math.min(amountBounds.value.max, playerBalance.value))
    // True when the wallet balance, not the house capacity, is what's actually
    // capping the top of the slider — the UI has to say so rather than
    // silently showing a lower ceiling than amountBounds implies.
    const balanceCapped = computed(() => playerBalance.value < amountBounds.value.max)
    // True only when there EXISTS a valid amount this wallet could bet at the
    // current rung — house-feasible AND the balance reaches at least the
    // dust-clearing minimum. False here is a DIFFERENT reason than the house
    // having no capacity: the house could cover this rung, the wallet can't.
    // canBet folds this in so the FLIP button can never stay enabled on a bet
    // the wallet cannot fund.
    const canAffordRung = computed(() => amountBounds.value.feasible && playerBalance.value >= amountBounds.value.min)
    /**
     * True when the CURRENT rung is unplayable but a SAFER one (lower win
     * chance) is affordable — i.e. the odds slider is the way out.
     *
     * Only claims this when it is actually true: it walks toward LOWER win
     * chances and asks whether any rung there is both house-feasible AND within
     * the balance. Telling a player to lower the win chance when nothing lower
     * helps either would just be a different wrong message.
     *
     * Direction matters and is easy to get backwards (it was, first time). The
     * minimum stake is ceil(dust*D/K) with D = win*10000 and K = (n-win)*edge:
     * a HIGH win chance makes D big and K small, so the minimum explodes — 6,464
     * sats at 95% against 331 at the bottom. Lowering the win chance shrinks it,
     * and lower win chance means a HIGHER ladder index, since the ladder
     * decreases in win rate as the index rises.
     */
    const lowerOddsWouldHelp = computed(() => {
      if (!tiersLoaded.value || canAffordRung.value) return false
      const ladder = currentSkinLadder.value
      for (let i = safeStep.value + 1; i < ladder.length; i++) {
        const b = amountBoundsForOdds(ladder[i], {
          edgeBps: oddsEdgeBps.value, dust: dust.value, capacity: capacity.value,
          railMin: betMin.value, railMax: betMax.value, topUpHeadroom: dust.value,
        })
        if (b.feasible && playerBalance.value >= b.min) return true
      }
      return false
    })
    // Only a valid bet is required now — the side defaults to 'random', so a
    // fresh player can flip immediately after load.
    const canFlip = computed(() => !isFlipping.value && canBet.value)

    // ── Data load ─────────────────────────────────────────────────────
    async function loadTiers() {
      try {
        const data = await getTiers()
        tiers.value = data.tiers
        maxAvailable.value = data.maxAvailable
        houseBankroll.value = data.houseBankroll ?? data.maxAvailable
        if (data.dust) dust.value = data.dust
        if (data.oddsEdgeBps !== undefined) oddsEdgeBps.value = data.oddsEdgeBps
        if (data.rakeType === 'percentage' || data.rakeType === 'flat') rakeType.value = data.rakeType
        if (data.rakeValue !== undefined) rakeValue.value = data.rakeValue
        houseReady.value = data.houseReady
        capacity.value = data.capacity ?? 0
        betMin.value = data.betMin ?? 331
        betMax.value = data.betMax ?? 50_000
        tiersLoaded.value = true
      } catch (e) {
        console.warn('Failed to load tiers:', e)
      }
    }

    // ── Stats recording ───────────────────────────────────────────────
    let sparkKey = 0
    function recordResult(won: boolean, payout: number, roll: number | null) {
      const tier = betAmount.value
      const net = won ? payout - tier : -tier

      // P&L
      sessionPnl.value += net
      allTimePnl.value += net
      try { localStorage.setItem(PNL_KEY, JSON.stringify(allTimePnl.value)) } catch { /* quota */ }

      // Streak
      if (won) {
        streak.value = streak.value.kind === 'win'
          ? { kind: 'win', count: streak.value.count + 1 }
          : { kind: 'win', count: 1 }
      } else {
        streak.value = streak.value.kind === 'loss'
          ? { kind: 'loss', count: streak.value.count + 1 }
          : { kind: 'loss', count: 1 }
      }

      // Sparkline — newest pushed to the end (renders right-to-left).
      sparkline.value.push({ won, key: ++sparkKey })
      if (sparkline.value.length > SPARKLINE_MAX) sparkline.value.shift()

      // Outcome for the skin (roll = the variable-odds value; side is vestigial
      // now that every bet is variable-odds — kept for the SkinState shape).
      outcome.value = { won, side: 'heads', roll }
      phase.value = 'resolved'

      // Slab
      if (slabTimer) clearTimeout(slabTimer)
      slab.value = { won, amount: net }
      const slabMs = isAutoRunning.value ? SLAB_MS_AUTO : SLAB_MS_MANUAL
      slabTimer = setTimeout(() => { slab.value = null }, slabMs)

      // Win flash — only in manual mode to avoid strobing during auto.
      if (won && !isAutoRunning.value) {
        winFlash.value = true
        setTimeout(() => { winFlash.value = false }, WIN_FLASH_MS)
      }
    }

    // ── Single flip ───────────────────────────────────────────────────
    /**
     * Commit one game. `overrideBet` lets a skin that owns its play gesture
     * (Rocket) pass the bet it locked in at cashout — which may differ from
     * the parent slider's current step. When absent, falls back to
     * `selectedBet.value` (the slider's current position).
     */
    async function flipOnce(overrideBet?: OddsBet): Promise<boolean> {
      if (!canBet.value) return false

      // Mark busy but DON'T animate yet — we only spin once the bet is
      // actually placed on the server, so a rejected bet (insufficient
      // balance, house busy, validation) never shows a phantom flip.
      isFlipping.value = true
      // For gesture-owning skins, keep the 'settling' phase so the skin
      // doesn't flicker through 'idle' between cashout and flipping.
      if (!skinOwnsGesture.value) {
        phase.value = 'idle'
        outcome.value = null
      }
      error.value = null

      try {
        if (store.state.ark.status !== 'connected') {
          throw new Error('Wallet not connected — open the wallet drawer to reconnect.')
        }

        const playerChangeAddress = store.getters['ark/address']
        if (!playerChangeAddress) throw new Error('No Ark address available — wallet still connecting?')

        phase.value = 'flipping'

        // Trustless settlement: the store action escrows the player's stake,
        // reveals, and (on a win) sweeps the pot — all on-Ark. Keep animating
        // for at least MIN_FLIP_MS so a fast resolution still reads as a flip.
        const bet = overrideBet ?? selectedBet.value
        // Defense-in-depth balance guard, same "universal choke point"
        // reasoning as the dust guard below. canBet already reflects this
        // (via canAffordRung), but runAuto() doesn't re-check canFlip between
        // iterations — this is what actually stops an auto-batch from
        // attempting a bet the wallet can no longer cover once the balance
        // has moved mid-run.
        if (betAmount.value > playerBalance.value) {
          throw new Error(`Your balance can't cover a ${betAmount.value.toLocaleString()}-sat bet.`)
        }
        // Defense-in-depth dust guard. The odds slider already clamps coin /
        // roulette bets to a dust-safe house stake, and the Rocket skin gates its
        // cash-out the same way — but a gesture skin's overrideBet is the one path
        // that can hand us a sub-dust bet, which the server (and the on-chain
        // escrow) would reject. This is the universal choke point: never place a
        // bet whose house side falls below the dust floor.
        if (houseStakeOf(bet) < dust.value) {
          throw new Error(
            `That win chance is too high for this stake — the house side would fall below the ${dust.value}-sat minimum. Lower the win chance / multiplier or raise the stake.`,
          )
        }
        const [result] = await Promise.all([
          // Routes to v0.4 (joint pot) or v0.3 (per-party escrow) by the server's
          // advertised protocolVersion — v0.4 is the default; PROTOCOL_VERSION=v3 opts out.
          store.dispatch('ark/placeTrustlessBet', {
            tier: betAmount.value,
            oddsN: bet.n, oddsTarget: bet.target, oddsLo: bet.lo,
          }),
          new Promise((r) => setTimeout(r, MIN_FLIP_MS)),
        ])

        recordResult(result.winner === 'player', result.payout, result.roll ?? null)

        const historyEntry = {
          id: `${Date.now()}`,
          tier: betAmount.value,
          playerChoice: stepLabel.value,
          winner: result.winner,
          rakeAmount: result.rake,
          payoutAmount: result.payout,
          status: 'resolved',
          createdAt: new Date().toISOString(),
          resolvedAt: new Date().toISOString(),
          // On-chain txids this game touched (v4 co-fund + settle), so the
          // SDK activity history can group its wallet transactions into one
          // row via the resolver registered in store/modules/ark.
          txids: [result.cofundTxid, result.settleTxid, result.txid].filter(Boolean),
          // What the game actually WAS. Without these the activity resolver has
          // nothing to label with and every game reads "Dice game" regardless of
          // which skin was played, with no odds to show.
          skinId: currentSkinId.value,
          skinName: currentSkin.value.name,
          odds: { n: bet.n, lo: bet.lo, target: bet.target },
        }
        const history = JSON.parse(localStorage.getItem('gameHistory') || '[]')
        history.unshift(historyEntry)
        localStorage.setItem('gameHistory', JSON.stringify(history.slice(0, 100)))

        // No manual deferred refresh — the SDK contract watcher pushes the
        // post-flip balance the instant the sweep's vtxos move (ark.ts).

        return true
      } catch (e: unknown) {
        // Log the raw cause for diagnostics; show the player a friendly message.
        const raw = getErrorMessage(e)
        logDiag('error', 'flip', raw)
        error.value = friendlyError(raw)
        phase.value = 'idle'
        outcome.value = null
        const shown = error.value
        setTimeout(() => { if (error.value === shown) error.value = null }, 8000)
        return false
      } finally {
        isFlipping.value = false
        // Re-read the envelope after EVERY attempt, settled or rejected. Each
        // accepted bet reserves house coins, so the capacity /api/tiers
        // advertised at mount is stale from the first flip onward — an auto
        // batch would then keep offering a maximum the house can no longer take
        // and die on a 400 at bet 2. Refreshing on the rejected path too is what
        // makes that recoverable without a page reload. Fire-and-forget: it
        // can't reject (loadTiers swallows), and the ~900ms auto pause covers it.
        loadTiers()
      }
    }

    function selectAuto(value: number | null) {
      if (isAutoRunning.value) return
      autoCount.value = value
    }

    async function runAuto() {
      if (autoCount.value === null) return
      isAutoRunning.value = true
      stopRequested.value = false
      autoRemaining.value = autoCount.value === Infinity ? Infinity : autoCount.value
      autoFlipsCounter.value = 0

      while (autoRemaining.value > 0 && !stopRequested.value) {
        const ok = await flipOnce()
        if (!ok) break
        autoFlipsCounter.value += 1
        if (isFinite(autoRemaining.value)) autoRemaining.value -= 1
        if (autoRemaining.value > 0 && !stopRequested.value) {
          await new Promise((resolve) => setTimeout(resolve, AUTO_FLIP_PAUSE_MS))
        }
      }

      isAutoRunning.value = false
      stopRequested.value = false
      phase.value = 'idle'
    }

    async function doFlip() {
      if (isAutoRunning.value) {
        stopRequested.value = true
        return
      }
      if (isAutoMode.value) {
        await runAuto()
        return
      }
      await flipOnce()
    }

    // ── Keyboard hotkey: Enter to re-flip ─────────────────────────────
    function handleKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      if (e.key !== 'Enter') return
      if (isFlipping.value || isAutoRunning.value) return
      if (!canFlip.value) return
      e.preventDefault()
      doFlip()
    }

    onMounted(async () => {
      window.addEventListener('keydown', handleKey)
      await loadTiers()

      if (store.state.ark.status !== 'connected' && store.state.ark.status !== 'connecting') {
        // Align to the server's network (env-driven) before connecting.
        store.dispatch('ark/syncNetworkFromServer').catch(() => { /* surfaced in drawer */ })
      }
      setTimeout(() => {
        const settled = Number(store.getters['ark/balance'] || 0)
        const boarding = Number(store.getters['ark/boardingBalance'] || 0)
        if (settled === 0 && boarding === 0) emit('open-wallet')
      }, 1500)
    })

    onUnmounted(() => {
      stopRequested.value = true
      window.removeEventListener('keydown', handleKey)
      if (slabTimer) clearTimeout(slabTimer)
    })

    // Switching skins swaps in that skin's own ladder — restore the step the
    // player last used on THAT skin (falling back to its default), clamped into
    // the playable [dust, capacity] window. Per-skin, because a step index only
    // means anything against one ladder: step 3 of the coin ladder and step 3 of
    // the roulette ladder are different bets entirely.
    watch(currentSkinId, (id) => {
      const want = getSavedStep(id) ?? currentSkin.value.defaultStep
      sliderIndex.value = Math.min(Math.max(want, minStep.value), maxStep.value)
    })
    // Keep the thumb inside the playable window when the stake changes — the
    // capacity ceiling drops and the dust floor rises as the amount moves.
    watch([minStep, maxStep], ([lo, hi]) => {
      sliderIndex.value = Math.min(Math.max(sliderIndex.value, lo), hi)
    })
    // Dual constraint: each control clamps the other into its new window, so
    // the displayed bet is always one the server will accept.
    watch([betAmount, currentSkinLadder, capacity], () => {
      const w = oddsWindow.value
      if (!w) return
      if (sliderIndex.value < w.loIndex) sliderIndex.value = w.loIndex
      else if (sliderIndex.value > w.hiIndex) sliderIndex.value = w.hiIndex
    })
    // Also re-clamps when the wallet balance itself moves (e.g. it drops after
    // a bet resolves) — otherwise a stale betAmount above the new balance
    // would sit unnoticed until the next slider/capacity change.
    //
    // A single clamp over [b.min, usableMax], NOT two independent branches —
    // two branches each checking against a different bound (b.min vs b.max)
    // let the low branch push betAmount UP past usableMax whenever the
    // wallet balance sits below this rung's dust-clearing minimum, silently
    // overwriting the balance ceiling. canAffordRung guards the case where
    // that range is empty (nothing to clamp into): leave betAmount alone —
    // canBet/flipOnce refuse the bet instead of the slider showing a
    // fabricated amount.
    watch([sliderIndex, capacity, playerBalance], () => {
      if (!canAffordRung.value) return
      const b = amountBounds.value
      betAmount.value = Math.min(Math.max(betAmount.value, b.min), usableMax.value)
    })

    // Remember where the player left both controls — but ONLY on their own
    // input, never on a clamp. Every other write to these refs above is the
    // envelope pulling them into range, and persisting that would let a
    // temporary dip in house capacity silently overwrite the player's chosen
    // stake: restore 45,000 → clamp to 1,928 → save 1,928, and the 45,000 is
    // gone for good. Saving only the gesture keeps the preference intact and
    // lets the clamp govern the display alone, so it comes back on its own once
    // capacity recovers. `change` (not `input`) also means one write per drag.
    function rememberAmount() { saveBetAmount(betAmount.value) }
    function rememberStep() { saveStep(currentSkinId.value, sliderIndex.value) }

    /** Grouped digits for the resting state of the bet field, e.g. "7,777". */
    function formatSats(n: number): string { return n.toLocaleString() }

    /**
     * A typed stake. Forced into the live envelope before it reaches
     * `betAmount` — the field deliberately has no idea what the bounds are, so
     * this is where an over-capacity or sub-minimum entry gets corrected. The
     * readout then re-renders to the clamped number, so the player sees what
     * they actually bet rather than what they typed.
     */
    function commitAmount(value: number) {
      const wanted = Math.round(value)
      betAmount.value = Math.min(Math.max(wanted, amountBounds.value.min), usableMax.value)
      rememberAmount()
    }

    /**
     * A typed win chance. Snapped to the nearest rung the house can currently
     * cover, which on a coarse ladder can be a visible jump (the coin skin has
     * only 6 rungs, so 32% lands on 25%). That is why the readout re-renders to
     * the snapped value instead of keeping the typed text: showing "32" while
     * betting 25% would misreport the bet.
     */
    function commitWinPct(pct: number) {
      sliderIndex.value = nearestRungByWinPct(
        currentSkinLadder.value, minStep.value, maxStep.value, pct,
      )
      rememberStep()
    }

    return {
      // Game config
      tiers, maxAvailable, houseReady, tiersLoaded,
      // Amount slider
      betAmount, amountBounds, usableMax, balanceCapped, canAffordRung, canBet,
      // Odds slider
      sliderIndex, minStep, maxStep, selectedBet, stepLabel, winPctValue, payoutMult,
      // Lifecycle
      isFlipping, error, skinState, phase, flipStageLabel, lowerOddsWouldHelp,
      // Auto
      autoOptions: AUTO_OPTIONS, autoCount, autoRemaining, isAutoRunning, isAutoMode, stopRequested,
      autoCountLabel, autoRemainingLabel, autoProgressLabel,
      winSatsLabel,
      // Preference persistence (user gesture only — see rememberAmount above)
      rememberAmount, rememberStep,
      // Typed entry — the sliders' precise alternative
      commitAmount, commitWinPct, formatSats,
      // Skin
      skins: SKINS, currentSkinId, currentSkin, selectSkin,
      skinOwnsGesture, onSkinLaunch, onSkinCashout,
      oddsEdgeBps, dust,
      // History modal
      historyOpen, historyGames, openHistory,
      detailOpen, detailGameId, openDetail, playerPubkey, networkName,
      // Stats
      sessionPnl, allTimePnl, pnlScope, togglePnlScope, formattedPnl, pnlClass,
      arkStatus, walletBalanceLabel,
      streak, sparkline, slab, winFlash,
      // Other
      playerBalance, canFlip,
      // Actions
      selectAuto, doFlip,
    }
  },
})
</script>

<style scoped>
.play-page {
  max-width: 520px;
  margin: 0 auto;
  /* The wallet balance now lives in the in-page top-hud (the global float-pill is
   *  hidden on this route), so the page no longer needs a tall top padding to clear
   *  a floating pill — reclaim it (~40px) so the whole game fits without scrolling. */
  padding: 14px 16px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  position: relative;
}

/* ── Top HUD ──────────────────────────────────────────────────────── */
.top-hud {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  /* Deliberately NO wrap. Wrapping put the wallet/help/history on a second row
     on a phone, which is half of what read as "things in different rows,
     jumping around": the row count changed with the viewport AND with the P/L
     width. The skin chips shrink instead (see .skin-selector) — a slightly
     tighter chip is a much smaller cost than a HUD whose shape moves. */
  flex-wrap: nowrap;
}

.skin-selector {
  display: flex;
  gap: 4px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-light);
  border-radius: 999px;
  padding: 3px;
  /* Never shrink or clip: a squeezed pill pushes its last chip (rocket) outside
     the rounded background. Row 1 fits without it because the P/L pill moved to
     the status row. */
  flex-shrink: 0;
}
.skin-chip {
  border: none;
  background: transparent;
  color: var(--text-muted);
  border-radius: 999px;
  padding: 5px 8px;
  cursor: pointer;
  font-size: 1.05rem;
  line-height: 1;
  transition: all 0.15s;
}
.skin-chip:hover { color: var(--text); }
.skin-chip.selected {
  background: rgba(247, 201, 72, 0.14);
  color: var(--gold);
  box-shadow: 0 0 8px var(--gold-glow);
}
.skin-icon { display: inline-block; }

.hud-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.history-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-light);
  color: var(--text-muted);
  text-decoration: none;
  font-size: 1.05rem;
  line-height: 1;
  transition: all 0.18s;
}
.history-btn:hover {
  color: var(--gold);
  border-color: var(--gold);
  box-shadow: 0 0 10px var(--gold-glow);
}

/* Wallet chip in the HUD (replaces the global float-pill on this route). */
.wallet-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 5px 12px;
  background: var(--bg-elevated);
  border: 1px solid var(--gold-dim, #d4a530);
  border-radius: 999px;
  color: var(--text);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s;
}
.wallet-chip:hover { border-color: var(--gold); box-shadow: 0 0 12px var(--gold-glow); }
.wallet-chip .wallet-ico { font-size: 0.9rem; line-height: 1; }
.wallet-chip .wallet-bal { color: var(--gold); }
.conn-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--text-muted);
  flex-shrink: 0;
}
.conn-dot.connected { background: var(--green, #22c55e); box-shadow: 0 0 6px rgba(34, 197, 94, 0.6); }
.conn-dot.connecting { background: var(--gold); }
.conn-dot.error { background: var(--red); }

.pnl-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-light);
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;
  user-select: none;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.18s;
}
.pnl-pill:hover { border-color: var(--text-muted); }
.pnl-scope {
  text-transform: uppercase;
  font-size: 0.62rem;
  letter-spacing: 1.5px;
  color: var(--text-muted);
  font-weight: 700;
}
.pnl-amount {
  font-family: ui-monospace, monospace;
  /* Tabular figures + a floor width so the pill does NOT resize as the number
     moves 0 -> +3,018 -> -8,724. Its width was changing after every single bet
     and dragging the whole HUD with it. */
  font-variant-numeric: tabular-nums;
  min-width: 7ch;
  text-align: right;
}
.pnl-pill.up .pnl-amount { color: var(--green, #22c55e); }
.pnl-pill.down .pnl-amount { color: var(--red); }
.pnl-pill.neutral .pnl-amount { color: var(--text-muted); }
.pnl-pill.up { border-color: rgba(34, 197, 94, 0.3); }
.pnl-pill.down { border-color: rgba(239, 68, 68, 0.3); }

/* ── Sparkline ──────────────────────────────────────────────────── */
.sparkline {
  /* Shares the status row with the P/L pill and the streak badge now, so it
     takes the leftover space rather than the full width. */
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 3px;
  /* Centred, not right-hugged: flush right it read as a detached strip floating
     away from the HUD rather than part of it. */
  justify-content: center;
  flex-wrap: nowrap;
  overflow: hidden;
  min-height: 14px;
}
.spark-dot {
  width: 6px;
  height: 14px;
  border-radius: 2px;
  flex-shrink: 0;
  animation: sparkIn 0.3s ease both;
}
.spark-dot.win {
  background: linear-gradient(180deg, var(--green, #22c55e) 0%, #14532d 100%);
  box-shadow: 0 0 4px rgba(34, 197, 94, 0.5);
}
.spark-dot.loss {
  background: linear-gradient(180deg, #7f1d1d 0%, var(--red) 100%);
  box-shadow: 0 0 4px rgba(239, 68, 68, 0.4);
}
@keyframes sparkIn {
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
}

/* ── Skin area ──────────────────────────────────────────────────── */
.skin-area {
  padding: 14px 0 16px;
  transition: filter 0.2s ease;
}
.skin-area.playing { filter: brightness(1.1); }

/* ── Streak badge ───────────────────────────────────────────────── */
.status-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  /* Holds its height with or without content, so a first result or a starting
     streak never pushes the centrepiece down. */
  min-height: 22px;
}
.streak-badge {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  z-index: 10;
}
.streak-badge.win {
  background: rgba(247, 201, 72, 0.15);
  color: var(--gold);
  border: 1px solid var(--gold);
  box-shadow: 0 0 12px var(--gold-glow);
  animation: streakPulse 1.4s ease-in-out infinite;
}
.streak-badge.loss {
  background: rgba(56, 189, 248, 0.12);
  color: var(--blue);
  border: 1px solid var(--blue);
}
.streak-icon { font-size: 0.9rem; }
@keyframes streakPulse {
  0%, 100% { box-shadow: 0 0 12px var(--gold-glow); }
  50% { box-shadow: 0 0 20px var(--gold-glow), 0 0 30px rgba(247, 201, 72, 0.3); }
}
.streak-pop-enter-active, .streak-pop-leave-active { transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.streak-pop-enter-from { opacity: 0; transform: scale(0.6) translateY(-6px); }
.streak-pop-leave-to { opacity: 0; transform: scale(0.8); }

/* ── Controls ───────────────────────────────────────────────────── */
.controls {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
  align-items: center;
  transition: opacity 0.18s ease;
}
.controls.inert {
  opacity: 0.35;
  pointer-events: none;
}

.control-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.auto-call-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-elevated);
  border: 1.5px dashed var(--gold);
  color: var(--gold);
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1.5px;
}
.dice-icon { font-size: 1.1rem; }

/* Auto chips */
.auto-selector {
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  max-width: 340px;
}
.auto-chip {
  flex: 1;
  min-width: 42px;
  background: var(--bg-elevated);
  border: 1.5px solid var(--border-light);
  color: var(--text-muted);
  border-radius: 8px;
  padding: 8px 4px;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.auto-chip:hover:not(:disabled) { border-color: var(--blue); color: var(--text); }
.auto-chip.selected {
  border-color: var(--gold);
  background: rgba(247, 201, 72, 0.1);
  color: var(--gold);
}
.auto-chip:disabled { opacity: 0.4; cursor: not-allowed; }

/* Amount slider */
.amount-slider {
  gap: 8px;
  max-width: 340px;
}
.amount-readout {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  white-space: nowrap;
}
.amount-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--text-muted);
}
.amount-value {
  font-size: 0.95rem;
  font-weight: 800;
  font-family: ui-monospace, monospace;
  color: var(--gold);
}
.amount-range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-light);
  outline: none;
  cursor: pointer;
}
.amount-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--bg);
  box-shadow: 0 0 10px var(--gold-glow);
  cursor: pointer;
}
.amount-range::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--bg);
  box-shadow: 0 0 10px var(--gold-glow);
  cursor: pointer;
}
.amount-range:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.amount-ends {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* .control-group is a centering flex column, so this collapses to content
     width without it and space-between has nothing to distribute — the min and
     max rail labels then render flush together ("3311,928"). Same fix as the
     sibling .amount-readout above. */
  width: 100%;
  font-size: 0.58rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--text-muted);
}
.amount-warn {
  color: var(--red);
  font-weight: 700;
}

/* Odds slider */
.odds-slider {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 340px;
}
.odds-readout {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.odds-step-label {
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: 1px;
  color: var(--gold);
  /* The row is label | stats. Without these the label wrapped and dragged the
     stats onto second lines with it, so "TOP 115 OF 128  90% win  1.09x -> 3,274
     sats" rendered as six fragments. The label yields first (it is the least
     load-bearing of the two) and truncates rather than wrapping. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.odds-readout .odds-stats {
  display: flex;
  align-items: baseline;
  gap: 8px;
  /* Never wrap or shrink: the win chance, multiple and payout are the numbers
     the bet is actually made on. */
  white-space: nowrap;
  flex-shrink: 0;
}
.win-pct {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text);
}
.payout-mult {
  font-size: 0.95rem;
  font-weight: 800;
  font-family: ui-monospace, monospace;
  color: var(--green, #22c55e);
}
.win-amt {
  font-size: 0.78rem;
  font-weight: 600;
  font-family: ui-monospace, monospace;
  color: var(--text-dim, #94a3b8);
  margin-left: 2px;
}
.odds-range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--green, #22c55e) 0%, var(--gold) 55%, var(--red) 100%);
  outline: none;
  cursor: pointer;
}
.odds-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--bg);
  box-shadow: 0 0 10px var(--gold-glow);
  cursor: pointer;
}
.odds-range::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--bg);
  box-shadow: 0 0 10px var(--gold-glow);
  cursor: pointer;
}
.odds-ends {
  display: flex;
  justify-content: space-between;
  font-size: 0.58rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--text-muted);
}

/* Flip button */
.flip-btn {
  width: 100%;
  max-width: 340px;
  background: var(--bg-elevated);
  border: 2px solid var(--border-light);
  color: var(--text-muted);
  border-radius: 12px;
  padding: 16px 36px;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: 3px;
  cursor: not-allowed;
  transition: all 0.2s;
  font-family: inherit;
}
.flip-btn.active {
  border-color: var(--gold);
  color: var(--gold);
  cursor: pointer;
  background: rgba(247, 201, 72, 0.06);
  box-shadow: 0 0 16px var(--gold-glow);
}
.flip-btn.active:hover {
  background: linear-gradient(135deg, var(--gold) 0%, var(--gold-dim) 100%);
  color: var(--bg);
  box-shadow: 0 4px 28px var(--gold-glow), 0 0 50px var(--gold-glow);
  transform: translateY(-1px);
}
.flip-btn.active:active { transform: translateY(0) scale(0.99); }
.flip-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.flip-btn.stop {
  border-color: var(--red);
  color: var(--red);
  cursor: pointer;
  background: rgba(239, 68, 68, 0.08);
  opacity: 1;
}
.flip-btn.stop:hover { background: var(--red); color: #fff; }
/* While the current flip finishes after STOP is clicked — pulses + disables
   the button so the user knows we got the click and are waiting for the
   in-flight game to settle before we exit autoplay. */
.flip-btn.stopping {
  border-color: var(--red);
  color: var(--red);
  background: rgba(239, 68, 68, 0.06);
  opacity: 1;
  cursor: progress;
  animation: stopping-pulse 1.2s ease-in-out infinite;
}
@keyframes stopping-pulse {
  0%, 100% { background: rgba(239, 68, 68, 0.06); }
  50%      { background: rgba(239, 68, 68, 0.14); }
}

.hotkey-hint {
  font-size: 0.62rem;
  color: var(--text-muted);
  letter-spacing: 2px;
  margin-top: -8px;
  opacity: 0.6;
}

/* Sits where the hotkey hint does, so adding it costs no height. */
.flip-stage-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.62rem;
  color: var(--text-muted);
  letter-spacing: 2px;
  margin-top: -8px;
}
/* A pulsing dot, not a spinner: the point is "still alive", and a pulse reads
   that at a glance without implying a determinate progress bar we can't honour
   (leg durations vary by an order of magnitude). */
.stage-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--gold);
  animation: stage-pulse 1.1s ease-in-out infinite;
}
@keyframes stage-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.25; transform: scale(0.7); }
}

/* ── Error toast ────────────────────────────────────────────────── */
.error-toast {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--red);
  color: #fff;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  z-index: 999;
  animation: slideUp 0.3s ease;
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);
  cursor: pointer;
  /* Wrap + cap so a long raw error stays on-screen instead of running off both
     edges (the old single-line toast clipped the message). Tap dismisses it. */
  max-width: min(92vw, 440px);
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
  max-height: 40vh;
  overflow-y: auto;
  text-align: center;
}

/* ── Win flash ──────────────────────────────────────────────────── */
.win-flash {
  position: fixed;
  inset: 0;
  background: radial-gradient(circle at 50% 40%, rgba(34, 197, 94, 0.18) 0%, transparent 70%);
  pointer-events: none;
  z-index: 100;
  animation: winFlashAnim 120ms ease-out;
}
@keyframes winFlashAnim {
  0%   { opacity: 0; }
  20%  { opacity: 1; }
  100% { opacity: 0; }
}

/* ── Result slab ────────────────────────────────────────────────── */
.result-slab {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 22px 24px 30px;
  text-align: center;
  z-index: 200;
  backdrop-filter: blur(8px);
}
.result-slab.win {
  background: linear-gradient(180deg, rgba(34, 197, 94, 0.18) 0%, rgba(20, 83, 45, 0.6) 100%);
  border-top: 1px solid rgba(34, 197, 94, 0.5);
}
.result-slab.loss {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12) 0%, rgba(127, 29, 29, 0.4) 100%);
  border-top: 1px solid rgba(239, 68, 68, 0.4);
}
.slab-label {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 4px;
  text-transform: uppercase;
}
.result-slab.win .slab-label { color: var(--green, #22c55e); font-size: 0.85rem; letter-spacing: 6px; }
.result-slab.loss .slab-label { color: var(--red); }
.slab-amount {
  margin-top: 6px;
  font-weight: 800;
  letter-spacing: 1px;
}
.result-slab.win .slab-amount { font-size: 2.2rem; color: var(--green, #22c55e); }
.result-slab.loss .slab-amount { font-size: 1.4rem; color: var(--red); }
.slab-unit { font-size: 0.65rem; color: var(--text-muted); margin-left: 4px; letter-spacing: 2px; }

.slab-enter-active, .slab-leave-active { transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s; }
.slab-enter-from { transform: translateY(100%); opacity: 0; }
.slab-leave-to { transform: translateY(100%); opacity: 0; }

/* ── History modal ──────────────────────────────────────────────── */
.hist-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.hist-modal {
  width: min(520px, 100%);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}
.hist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.hist-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--text);
}
.hist-close {
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.15s;
}
.hist-close:hover { color: var(--text); }
/* Only the list scrolls — the header stays pinned. */
.hist-body {
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;
}
.hist-fade-enter-active, .hist-fade-leave-active { transition: opacity 0.2s; }
.hist-fade-enter-from, .hist-fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .side-btn { padding: 12px 10px; font-size: 0.8rem; }
  .flip-btn { padding: 14px 28px; font-size: 0.95rem; }
  .streak-badge { top: 100px; right: 10px; }
  .result-slab.win .slab-amount { font-size: 1.8rem; }

  /* Tight HUD: at <520px the skin chips + pnl pill + 2 round buttons
     together exceeded the viewport, pushing the right side off-screen.
     Shrink chip padding, drop the pnl 'SESSION' label, hide help button
     (still reachable via /how-it-works directly). */
  /* HORIZONTAL ONLY. This was `padding: 48px 12px 80px`, and the shorthand was
     the bug: it reset all four sides, so it silently clobbered the vertical
     padding the short-viewport block at the end of this file sets to keep the
     FLIP button above the fold. The 48/80 were themselves left over from the
     floating balance pill that this route no longer shows. */
  .play-page { padding-left: 12px; padding-right: 12px; }
  .skin-chip { padding: 5px 8px; font-size: 1rem; }
  .pnl-pill { padding: 5px 10px; font-size: 0.8rem; gap: 6px; }
  .pnl-scope { display: none; }
  .history-btn { width: 30px; height: 30px; font-size: 0.95rem; }
}

@media (max-width: 380px) {
  /* Very narrow phones — even tighter. Cut chip padding + button size further.
     Keep the help button visible — the v0.3.8 fix hid it entirely, but with
     the rest of the HUD tightened the help button still fits and is the only
     entry point to /how-it-works for users who haven't bookmarked it. */
  .skin-chip { padding: 5px 6px; font-size: 0.95rem; }
  .history-btn { width: 28px; height: 28px; font-size: 0.85rem; }
  .pnl-pill { padding: 4px 8px; font-size: 0.75rem; }
}

/* Auto-chip row should always fit and wrap if it can't. The base CSS already
   sets flex-wrap; this just lets each chip shrink past its 42px min-width on
   narrow viewports rather than overflowing the parent. */
@media (max-width: 520px) {
  .auto-chip { min-width: 0; padding: 8px 2px; font-size: 0.72rem; }
}

/* Slider end labels: 'RISKIER · BIGGER PAYOUT' is borderline at 380px. Allow
   the right end to wrap to two lines instead of clipping. */
@media (max-width: 420px) {
  .odds-ends { font-size: 0.54rem; }
  .odds-ends span:last-child { text-align: right; }
}

/* Short viewports (a phone with browser chrome showing): tighten the vertical
   rhythm so the bet slider, odds slider, auto-play row and FLIP button all stay
   above the fold. The die shrinks via a viewport-relative height in the skin.
 *
 * MUST BE LAST. Media queries add no specificity, so these rules only beat the
 * base `.play-page` / `.skin-area` declarations by document order. This block
 * previously sat near the top of the file and lost to both of them, which put
 * the FLIP button ~30px below the fold on a 390x664 viewport -- the whole
 * reason the page scrolled on mobile at all. Measured: 774px -> 664px. */
@media (max-height: 720px) {
  .play-page {
    gap: 10px;
    padding-top: 12px;
    padding-bottom: 16px;
  }
  .skin-area { padding: 6px 0 8px; }
}

/* Very short viewports (iPhone SE-class, ~375x553 once browser chrome is
   showing). The die has its own tighter tier at this height (DiceSkin.vue) and
   is already down to ~155px, so take the remaining slack from the gaps between
   control groups instead -- 3 gaps x 18px is the largest remaining block of
   whitespace. Measured at 375x553: 21px of overflow -> 0. */
@media (max-height: 640px) {
  .controls { gap: 10px; }
}
</style>
